import { cn } from "@/lib/utils";

type Variant = "diamonds" | "triangles" | "adinkra" | "kente";

/**
 * Decorative SVG patterns for the ChitChat surface.
 * Rendered as positioned overlays — set `opacity` per spec.
 */
export function AfroPattern({
  variant,
  opacity = 0.08,
  color = "currentColor",
  className,
}: {
  variant: Variant;
  opacity?: number;
  color?: string;
  className?: string;
}) {
  const id = `cc-pat-${variant}`;
  return (
    <svg
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 h-full w-full", className)}
      style={{ opacity }}
    >
      <defs>
        {variant === "diamonds" && (
          <pattern id={id} x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
            <g fill="none" stroke={color} strokeWidth="1">
              <rect x="12" y="12" width="24" height="24" transform="rotate(45 24 24)" />
              <circle cx="24" cy="24" r="3" />
              <path d="M0 24 L8 16 L16 24 L8 32 Z" />
            </g>
          </pattern>
        )}
        {variant === "triangles" && (
          <pattern id={id} x="0" y="0" width="36" height="32" patternUnits="userSpaceOnUse">
            <g fill={color} fillOpacity="0.6">
              <polygon points="0,32 18,0 36,32" />
              <polygon points="9,32 18,16 27,32" fill={color} fillOpacity="1" />
            </g>
          </pattern>
        )}
        {variant === "adinkra" && (
          <pattern id={id} x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
            <g fill="none" stroke={color} strokeWidth="1">
              <circle cx="60" cy="60" r="44" />
              <circle cx="60" cy="60" r="30" />
              <circle cx="60" cy="60" r="16" />
              <circle cx="60" cy="60" r="3" fill={color} />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI) / 6;
                const x1 = 60 + Math.cos(a) * 18;
                const y1 = 60 + Math.sin(a) * 18;
                const x2 = 60 + Math.cos(a) * 28;
                const y2 = 60 + Math.sin(a) * 28;
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
              })}
            </g>
          </pattern>
        )}
        {variant === "kente" && (
          <pattern id={id} x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
            <g fill="none" stroke={color} strokeWidth="1.2">
              <rect x="8" y="8" width="16" height="16" transform="rotate(45 16 16)" />
              <line x1="0" y1="16" x2="32" y2="16" />
              <line x1="16" y1="0" x2="16" y2="32" />
            </g>
          </pattern>
        )}
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}
